import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { FiShare2 } from 'react-icons/fi';
import Notiflix from 'notiflix';
import { SharedCard } from './types';
import { useCardSharing } from '../../../hooks/useCardSharing';
import SharedCardItem from './SharedCardItem';
import CreateSharingModal from './CreateSharingModal';
import {
  SharedCardsSection,
  SharedCardsSectionTitle,
  SharedCardsList as StyledSharedCardsList,
  EmptyState,
  SectionHeader,
  ShareButton,
} from './styles';
import { CardInfoItem, ListBeneficiaryAndDependentResponse } from '..';

import { InsuredWallet } from '../../../services/cardSharingService';
import { useOnlineMode } from '../../../hooks/useOnlineMode';

export interface SharedCardsListProps {
  beneficiaryInfo: CardInfoItem;
  dependentsCardsInfo: ListBeneficiaryAndDependentResponse[];
  selectedBeneficiaryId?: string;
  isBeneficiaryLoading?: boolean;
}

const SharedCardsList: React.FC<SharedCardsListProps> = ({
  beneficiaryInfo,
  dependentsCardsInfo,
  selectedBeneficiaryId,
  isBeneficiaryLoading = false,
}) => {
  const {
    sharedCards,
    loading,
    error,
    deleteSharing,
    refreshData,
    clearError,
  } = useCardSharing(selectedBeneficiaryId);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { isOnline } = useOnlineMode();

  const handleUpdateCard = useCallback(() => {
    refreshData(selectedBeneficiaryId);
  }, [refreshData, selectedBeneficiaryId]);

  useEffect(() => {
    if (isBeneficiaryLoading) {
      Notiflix.Block.circle('.notiflix-loading-button-create-shared-card');
    } else {
      Notiflix.Block.remove('.notiflix-loading-button-create-shared-card');
    }
  }, [isBeneficiaryLoading]);

  const handleDeleteSharing = useCallback(
    async (token: string) => {
      try {
        await deleteSharing(token);
      } catch (tryErr) {
        console.error('Erro ao excluir compartilhamento:', tryErr);
      }
    },
    [deleteSharing],
  );

  const cardsWithSharedPeople = sharedCards.filter(
    (card: SharedCard) => card.sharedWith.length > 0,
  );

  const insuredWalletForAdd: InsuredWallet = useMemo(() => {
    const planInfo = dependentsCardsInfo.find(
      (x) => x.nrCartao === beneficiaryInfo.userPlanId,
    );
    return {
      planName: beneficiaryInfo.descRegulation,
      cardExpirateDate: beneficiaryInfo.cardExpirateDate,
      cardNumber: beneficiaryInfo.userPlanId,
      userUnimedId: planInfo?.userUnimedId.toString() ?? '',
      insuredName: beneficiaryInfo.beneficiaryName,
    };
  }, [
    dependentsCardsInfo,
    beneficiaryInfo.descRegulation,
    beneficiaryInfo.cardExpirateDate,
    beneficiaryInfo.userPlanId,
    beneficiaryInfo.beneficiaryName,
  ]);

  if (error) {
    return (
      <SharedCardsSection>
        <SharedCardsSectionTitle>Compartilhamentos</SharedCardsSectionTitle>
        <EmptyState>
          <div style={{ color: '#d32f2f', marginBottom: '1rem' }}>
            {isOnline
              ? 'Erro ao carregar compartilhamentos do cartão'
              : 'Informação indisponivel offline, tente novamente mais tarde'}
          </div>
          {isOnline && (
            <button
              type="button"
              onClick={() => {
                clearError();
                refreshData(selectedBeneficiaryId);
              }}
              style={{
                background: '#00995D',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Tentar novamente
            </button>
          )}
        </EmptyState>
      </SharedCardsSection>
    );
  }

  return (
    <SharedCardsSection>
      <SectionHeader>
        <SharedCardsSectionTitle style={{ marginBottom: 0 }}>
          Compartilhamentos
          {loading && (
            <span
              style={{
                marginLeft: '1rem',
                fontSize: '0.9rem',
                color: '#666',
                fontWeight: 'normal',
              }}
            />
          )}
        </SharedCardsSectionTitle>

        {isOnline && (
          <ShareButton
            type="button"
            className="notiflix-loading-button-create-shared-card"
            onClick={() => setIsCreateModalOpen(true)}
            disabled={isBeneficiaryLoading}
          >
            <FiShare2 style={{ marginRight: '0.5rem' }} />
            Compartilhar Cartão
          </ShareButton>
        )}
      </SectionHeader>

      {cardsWithSharedPeople.length > 0 ? (
        <StyledSharedCardsList>
          {cardsWithSharedPeople.map((card: SharedCard) => (
            <SharedCardItem
              key={card.id}
              card={card}
              onUpdateCard={handleUpdateCard}
              onDeleteSharing={handleDeleteSharing}
            />
          ))}
        </StyledSharedCardsList>
      ) : (
        <EmptyState>
          {loading ? (
            'Carregando compartilhamentos...'
          ) : (
            <>
              Você ainda não compartilhou nenhum cartão.
              <br />
              Use o botão &quot;Compartilhar&quot; acima para começar.
            </>
          )}
        </EmptyState>
      )}

      <CreateSharingModal
        isOpen={isCreateModalOpen}
        selectedCard={insuredWalletForAdd}
        onClose={() => setIsCreateModalOpen(false)}
        onRequestRefetch={handleUpdateCard}
      />
    </SharedCardsSection>
  );
};

export default SharedCardsList;
